import type { Pose, SettledMode, Transition } from './types';
import { TRANSITION } from '../core/config';
import { lerp, easeInOutCubic } from '../core/math';

export function createTransition(
  from: Pose,
  to: Pose,
  targetMode: SettledMode = 'fly',
  endsAtPainterView = false
): Transition {
  return {
    from,
    to,
    elapsed: 0,
    duration: TRANSITION.durationS,
    targetMode,
    endsAtPainterView,
  };
}

export function updateTransition(transition: Transition, dt: number): Transition {
  const newElapsed = Math.min(transition.elapsed + dt, transition.duration);
  return { ...transition, elapsed: newElapsed };
}

export function isTransitionComplete(transition: Transition): boolean {
  return transition.elapsed >= transition.duration;
}

function quadraticBezier(p0: number, p1: number, p2: number, t: number): number {
  const oneMinusT = 1 - t;
  return oneMinusT * oneMinusT * p0 + 2 * oneMinusT * t * p1 + t * t * p2;
}

function slerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > Math.PI) diff -= 2 * Math.PI;
  while (diff < -Math.PI) diff += 2 * Math.PI;
  return a + diff * t;
}

export function interpolatePose(transition: Transition): Pose {
  const { from, to, elapsed, duration } = transition;
  const rawT = elapsed / duration;
  const t = easeInOutCubic(rawT);

  const fromPos = from.position;
  const toPos = to.position;

  const midX = (fromPos[0] + toPos[0]) / 2;
  const midZ = (fromPos[2] + toPos[2]) / 2;
  const controlY = Math.max(fromPos[1], toPos[1]) + (transition.arcLiftM ?? TRANSITION.arcLiftM);

  const posX = quadraticBezier(fromPos[0], midX, toPos[0], t);
  const posY = quadraticBezier(fromPos[1], controlY, toPos[1], t);
  const posZ = quadraticBezier(fromPos[2], midZ, toPos[2], t);

  const yaw = slerpAngle(from.yaw, to.yaw, t);
  const pitch = lerp(from.pitch, to.pitch, t);
  const roll = lerp(from.roll, to.roll, t);
  const fovYDeg = lerp(from.fovYDeg, to.fovYDeg, t);

  return {
    position: [posX, posY, posZ],
    yaw,
    pitch,
    roll,
    fovYDeg,
  };
}
